// CVECard.jsx
import PropTypes from "prop-types";
import { format } from "date-fns";
import { enUS } from "date-fns/locale";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faBug,
  faExternalLinkAlt,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import { parseCVSSv3, parseCVSSv4, parseCVSSv2, explainCVSSMetricsAny } from "../utils/cvss";
import { useState, isValidElement, cloneElement } from "react";
import cweList from "../utils/cwe.json";
import CvssDetailModal from "./CvssDetailModal";

// CWE categorization
const getCWECategory = (cweId) => {
  if (!cweId) return cweId;
  const cwe = cweList.find((item) => item.ID === cweId);
  return cwe ? cwe.Name : cweId;
};

const CVECard = ({ cve }) => {
  // Basic formatting
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "MMM dd, yyyy", { locale: enUS });
    } catch (e) {
      console.error("Date formatting error:", e, dateString);
      return dateString;
    }
  };

  // Prefer normalized CVSS from parent; fallback to parsing new API fields directly
  const normalized = cve.cvss || null;

  // Build a fallback CVSS object from raw API fields if normalized is missing
  const buildFallback = () => {
    const has = (v) => v !== undefined && v !== null && String(v).trim() !== "";
    const num = (v) => (has(v) ? Number(v) : null);
    if (has(cve.cvss40) || has(cve.cvss40_score) || has(cve.cvss40_severity)) {
      return {
        version: "4.0",
        vector: has(cve.cvss40) ? cve.cvss40 : null,
        severity: cve.cvss40_severity || null,
        score: num(cve.cvss40_score),
      };
    }
    if (has(cve.cvss31) || has(cve.cvss31_score) || has(cve.cvss31_severity)) {
      return {
        version: "3.1",
        vector: has(cve.cvss31) ? cve.cvss31 : null,
        severity: cve.cvss31_severity || null,
        score: num(cve.cvss31_score),
      };
    }
    if (has(cve.cvss30) || has(cve.cvss30_score) || has(cve.cvss30_severity)) {
      return {
        version: "3.0",
        vector: has(cve.cvss30) ? cve.cvss30 : null,
        severity: cve.cvss30_severity || null,
        score: num(cve.cvss30_score),
      };
    }
    if (has(cve.cvss2) || has(cve.cvss2_score) || has(cve.cvss2_severity)) {
      return {
        version: "2.0",
        vector: has(cve.cvss2) ? cve.cvss2 : null,
        severity: cve.cvss2_severity || null,
        score: num(cve.cvss2_score),
      };
    }
    return null;
  };

  const fallback = normalized ? null : buildFallback();
  const versionStr = String(normalized?.version || fallback?.version || "");
  let parsedForMetrics = null;
  const vectorForParse = normalized?.vector || fallback?.vector;
  if (vectorForParse) {
    if (versionStr.startsWith("4")) parsedForMetrics = parseCVSSv4(vectorForParse);
    else if (versionStr.startsWith("3")) parsedForMetrics = parseCVSSv3(vectorForParse);
    else if (versionStr.startsWith("2")) parsedForMetrics = parseCVSSv2(vectorForParse);
  }

  const display = {
    severity: (normalized?.severity || fallback?.severity || "").toUpperCase() || undefined,
    score:
      typeof normalized?.score === "number"
        ? normalized.score
        : typeof fallback?.score === "number"
        ? fallback.score
        : undefined,
    version: normalized?.version || fallback?.version || "3.1",
    vector: normalized?.vector || fallback?.vector,
  };

  // Derive severity from score when the label is missing (e.g. old CVSS v2 entries)
  if (!display.severity && typeof display.score === "number") {
    if (display.score >= 9.0)      display.severity = "CRITICAL";
    else if (display.score >= 7.0) display.severity = "HIGH";
    else if (display.score >= 4.0) display.severity = "MEDIUM";
    else if (display.score > 0)    display.severity = "LOW";
  }

  // Prepare CVSS metrics for detail modal and determine if we actually have details to show
  const metricsList =
    explainCVSSMetricsAny(parsedForMetrics?.metrics || [], display.version) || [];
  const detailedMetrics = metricsList.filter(
    (m) => m && typeof m.description === "string" && m.description.trim() !== ""
  );
  const isV4 = String(display.version || "").startsWith("4");
  const showCvssDetails = !isV4 || detailedMetrics.length > 0;

  // --- Description show more/less ---
  const MAX_DESC_CHARS = 300;

  const normalizeNodes = (value) => {
    if (value == null) return [];
    if (typeof value === "string") return [value];
    if (Array.isArray(value)) return value;
    if (isValidElement(value)) return [value];
    return [String(value)];
  };

  const extractText = (nodes) => {
    let text = "";
    const walk = (n) => {
      if (n == null) return;
      if (typeof n === "string") {
        text += n;
      } else if (Array.isArray(n)) {
        n.forEach(walk);
      } else if (isValidElement(n)) {
        walk(n.props?.children);
      }
    };
    walk(nodes);
    return text;
  };

  const truncateNodes = (nodes, limit) => {
    let remaining = limit;
    const out = [];

    const cut = (n, keyPrefix = "") => {
      if (remaining <= 0 || n == null) return;
      if (typeof n === "string") {
        if (n.length <= remaining) {
          out.push(n);
          remaining -= n.length;
        } else {
          out.push(n.slice(0, remaining));
          remaining = 0;
        }
        return;
      }
      if (Array.isArray(n)) {
        for (let i = 0; i < n.length && remaining > 0; i++) {
          cut(n[i], `${keyPrefix}-${i}`);
        }
        return;
      }
      if (isValidElement(n)) {
        const child = n.props?.children;
        const childText = typeof child === "string" ? child : extractText(child);
        if (!childText) {
          out.push(n);
          return;
        }
        const take = childText.length <= remaining ? childText : childText.slice(0, remaining);
        remaining -= Math.min(childText.length, remaining);
        out.push(cloneElement(n, { key: `${keyPrefix}-clone` }, take));
        return;
      }
      // Fallback: push as string
      out.push(String(n));
    };

    cut(nodes);
    if (remaining <= 0) out.push("…");
    return out;
  };

  const descriptionNodes = normalizeNodes(cve.description);
  const descriptionText = extractText(descriptionNodes);
  const isLongDescription = descriptionText.length > MAX_DESC_CHARS;
  const [descExpanded, setDescExpanded] = useState(false);
  const visibleDescription = descExpanded || !isLongDescription
    ? descriptionNodes
    : truncateNodes(descriptionNodes, MAX_DESC_CHARS);
  const [showDetails, setShowDetails] = useState(false);


  // Determine exploit status with enhanced styling
  // Only show if exploit_active is true
  const exploitStatus = cve.exploit_active ? (
    <div className="flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 border border-red-200">
      <FontAwesomeIcon icon={faExclamationTriangle} className="h-3 w-3" />
      <span>Exploit Active</span>
    </div>
  ) : null;

  // Group CWEs by category
  const cweCategories = cve.CveCweIds
    ? cve.CveCweIds.reduce((acc, cwe) => {
        const category = getCWECategory(cwe.cwe_id);
        if (!acc[category]) acc[category] = [];
        acc[category].push(cwe.cwe_id);
        return acc;
      }, {})
    : {};

  return (
    <div className="flex flex-1 group hover:no-underline focus:outline-none">
      <div className="relative flex flex-col h-auto min-h-[12rem] border border-gray-200 w-full rounded-lg hover:border-blue-500 transition-all duration-300 shadow-sm hover:shadow-lg bg-white">
        {/* Header with gradient background */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b border-gray-100">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faBug} className="h-4 w-4 text-blue-600" />
              <h2 className="font-sans text-lg sm:text-xl font-bold group-hover:text-blue-600 transition-colors duration-300">
                {cve.highlightedId}
              </h2>
            </div>
            <div className="flex items-center gap-3">
              {exploitStatus}
           
              {(display.severity || display.score) && (
                <div className="flex items-center">
                  <div className="relative group/cvss">
                    <div className="flex items-center bg-white rounded-full shadow-sm overflow-hidden border border-gray-200 hover:shadow-md transition-shadow duration-200">
                      <span
                        className={`px-3 py-1.5 text-xs font-semibold flex items-center ${
                          display.severity === "CRITICAL"
                            ? "bg-gradient-to-r from-red-500 to-red-600 text-white"
                            : display.severity === "HIGH"
                            ? "bg-gradient-to-r from-orange-500 to-orange-600 text-white"
                            : display.severity === "MEDIUM"
                            ? "bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-800"
                            : "bg-gradient-to-r from-green-500 to-green-600 text-white"
                        }`}
                      >
                        {display.severity || "LOW"}{" "}
                        <span className="font-mono ml-1">
                          {display.score ?? "N/A"}
                        </span>
                        <span className="ml-2 text-[10px] opacity-80">v{display.version}</span>
                      </span>
                      <div className="h-4 w-px bg-gray-300"></div>
                      <button
                        className={`px-2.5 py-1.5 text-xs transition-colors duration-200 ${
                          display.severity === "CRITICAL"
                            ? "text-red-500 hover:bg-red-50"
                            : display.severity === "HIGH"
                            ? "text-orange-500 hover:bg-orange-50"
                            : display.severity === "MEDIUM"
                            ? "text-yellow-500 hover:bg-yellow-50"
                            : "text-green-500 hover:bg-green-50"
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowDetails(true);
                        }}
                        title="Show details"
                      >
                        <FontAwesomeIcon
                          icon={faChevronDown}
                          className="h-3 w-3 transition-transform duration-200"
                        />
                      </button>
                    </div>

                    {/* CVSS Tooltip */}
<div className="absolute top-full mt-2 z-50 opacity-0 invisible group-hover/cvss:opacity-100 group-hover/cvss:visible w-[260px] sm:w-[300px] max-w-[min(88vw,260px)] sm:max-w-[min(90vw,320px)] transition-all duration-200 transform -translate-y-1 group-hover/cvss:translate-y-0 left-0 sm:left-auto sm:right-0 origin-top-left sm:origin-top-right">                      <div className="bg-gray-800 text-white text-sm rounded-lg shadow-xl p-4">
                        <div className="flex items-start justify-between">
                          <h3 className="font-semibold text-[15px]">
                            CVSS v{display.version} Score Details
                          </h3>
                          <span
                            className={`px-2 py-0.5 text-xs rounded-full ${
                              display.severity === "CRITICAL"
                                ? "bg-gradient-to-r from-red-500 to-red-600"
                                : display.severity === "HIGH"
                                ? "bg-gradient-to-r from-orange-500 to-orange-600"
                                : display.severity === "MEDIUM"
                                ? "bg-gradient-to-r from-yellow-400 to-yellow-500"
                                : "bg-gradient-to-r from-green-500 to-green-600"
                            }`}
                          >
                            {display.score ?? "N/A"} ({display.severity || "LOW"})
                          </span>
                        </div>

                        <p className="text-gray-300 mt-2 leading-snug">
                          The Common Vulnerability Scoring System (CVSS)
                          provides a way to capture the principal
                          characteristics of a vulnerability.
                          <span className="block mt-1 font-medium">
                            {display.severity === "CRITICAL"
                              ? "🚨 Critical Impact"
                              : display.severity === "HIGH"
                              ? "⚠️ High Impact"
                              : display.severity === "MEDIUM"
                              ? "⚠️ Moderate Impact"
                              : "✅ Low Impact"}
                          </span>
                        </p>
                        <p className="text-gray-300 mt-2 leading-snug">Click the down arrow to view the details</p>
                      </div>

                      {/* Tooltip arrow */}
 <div className="absolute bottom-full left-3 sm:left-auto sm:right-3 w-0 h-0 border-l-6 border-r-6 border-b-6 border-l-transparent border-r-transparent border-b-gray-800"></div>                    </div>
                  </div>
                </div>
              )}
              {/* EPS Score Display */}

              {cve.eps && cve.eps.Score > 0 && (
                <div className="flex items-center ml-2">
                  {(() => {
                    const score = Number(cve.eps.Score);
                    let bgColor = "";
                    let borderColor = "";

                    if (score > 0.7) {
                      bgColor = "from-red-500 to-red-600";
                      borderColor = "border-red-200";
                    } else if (score > 0.4) {
                      bgColor = "from-orange-400 to-orange-500";
                      borderColor = "border-orange-200";
                    } else if (score > 0.1) {
                      bgColor = "from-yellow-400 to-yellow-500 text-black";
                      borderColor = "border-yellow-300";
                    } else {
                      bgColor = "from-blue-500 to-blue-600";
                      borderColor = "border-blue-200";
                    }

                    return (
                      <div className="relative inline-flex">
                        {/* EPS Badge with tooltip trigger */}
                        <div className="relative group/eps">
                          <span
                            className={`px-3 py-1.5 text-xs font-semibold flex items-center bg-gradient-to-r ${bgColor} text-white rounded-full border ${borderColor} cursor-help transition-all duration-200 shadow-sm hover:shadow-md hover:scale-[1.02]`}
                          >
                            EPS{" "}
                            <span className="font-mono ml-1">
                              {score.toFixed(5)}
                            </span>
                          </span>

                          {/* Tooltip - place below to avoid clipping at top of viewport */}
                          <div className="absolute top-full left-0 sm:left-auto sm:right-0 translate-x-0 mt-2 z-50 opacity-0 invisible group-hover/eps:opacity-100 group-hover/eps:visible w-[260px] sm:w-[300px] max-w-[min(88vw,260px)] sm:max-w-[min(90vw,320px)] transition-all duration-200 transform -translate-y-1 group-hover/eps:translate-y-0 origin-top-left sm:origin-top-right">
                            <div className="bg-gray-800 text-white text-sm rounded-lg shadow-xl p-4">
                              <div className="flex items-start justify-between">
                                <h3 className="font-semibold text-[15px]">
                                  Exploit Prediction Score (EPS)
                                </h3>
                                <span
                                  className={`px-2 py-0.5 text-xs rounded-full ${bgColor} bg-opacity-80`}
                                >
                                  {score.toFixed(5)}
                                </span>
                              </div>

                              <p className="text-gray-300 mt-2 leading-snug">
                                The Exploit Prediction Scoring System (EPS)
                                indicates how likely this vulnerability is to be
                                exploited.
                                <span className="block mt-1 font-medium">
                                  {score >= 0.75
                                    ? "🚨 Critical Risk"
                                    : score >= 0.5
                                    ? "⚠️ High Risk"
                                    : score >= 0.25
                                    ? "⚠️ Moderate Risk"
                                    : "✅ Low Risk"}
                                </span>
                              </p>

                              <div className="flex justify-between items-center pt-3 mt-2 border-t border-gray-700">
                                <a
                                  href="https://www.first.org/epss/"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-400 hover:text-blue-300 transition-colors text-sm flex items-center"
                                >
                                  Learn more
                                  <svg
                                    className="w-3.5 h-3.5 ml-1"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                    />
                                  </svg>
                                </a>
                                <span className="text-xs text-gray-400">
                                  Updated: {new Date().toLocaleDateString()}
                                </span>
                              </div>
                            </div>

                            {/* Tooltip arrow */}
                            <div className="absolute bottom-full left-3 sm:left-auto sm:right-3 w-0 h-0 border-l-6 border-r-6 border-b-6 border-l-transparent border-r-transparent border-b-gray-800"></div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3 p-4 sm:p-6 w-full">
          {/* Modal for CVSS details */}
          <CvssDetailModal
            isOpen={showDetails}
            onClose={() => setShowDetails(false)}
            title={`CVSS Details for ${cve.code || cve.id}`}
          >
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700 bg-gradient-to-r from-blue-50 to-blue-100 px-4 py-2 rounded-lg border border-blue-200">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 text-blue-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
                CVSS v{display.version} Metric Breakdown
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {showCvssDetails &&
                  detailedMetrics.map((m, i) => (
                    <div
                      key={i}
                      className="bg-white p-3 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors shadow-xs group/card relative"
                    >
                      <div className="flex items-start gap-2">
                        <div className="relative group/tooltip">
                          <div
                            className={`flex-shrink-0 mt-0.5 w-5 h-5 rounded-full cursor-help flex items-center justify-center text-white text-xs font-bold opacity-0 group-hover/card:opacity-100 transition-opacity duration-200 ${
                              m.group === 'impact'
                                ? 'bg-purple-500'
                                : m.group === 'exploitability'
                                ? 'bg-orange-500'
                                : 'bg-blue-500'
                            }`}
                          >
                            ?
                          </div>

                          {/* Tooltip content positioned at the blue dot */}
                          <div className="absolute bottom-full left-0 mb-2 px-3 py-2 text-xs bg-gray-900 text-white rounded-lg shadow-lg opacity-0 invisible group-hover/tooltip:opacity-100 group-hover/tooltip:visible transition-all duration-200 z-20 w-64">
                            <p className="font-semibold mb-0.5">{m.title}</p>
                            <p>{m.description}</p>
                            <div className="absolute top-full left-2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                          </div>
                        </div>

                        <div>
                          <div className="flex items-baseline gap-2">
                            <span className="font-semibold text-gray-800">
                              {m.key}
                              <span className="text-xs font-normal text-gray-500 ml-1">({m.title})</span>
                            </span>
                            <span className="text-xs font-medium px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
                              {m.label}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600 mt-1">{m.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
              {showCvssDetails && (
                <div className="bg-gray-50 px-4 py-2 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 text-gray-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                      />
                    </svg>
                    <span className="text-sm font-medium text-gray-700">
                      Vector String:
                    </span>
                  </div>
                  <code className="mt-1 block text-xs bg-gray-100 px-2 py-1 rounded text-gray-800 font-mono break-all">
                    {display.vector || "N/A"}
                  </code>
                </div>
              )}
            </div>
          </CvssDetailModal>
          {/* Dates with icons */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 text-gray-600 text-sm">
            <div className="flex items-center gap-2">
              <span className="font-medium">Published:</span>
              <time className="text-gray-700">
                {formatDate(cve.published_at)}
              </time>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-medium">Updated:</span>
              <time className="text-gray-700">
                {formatDate(cve.updated_at)}
              </time>
            </div>
          </div>

          {/* Description with show more/less */}
          <div className="font-sans text-gray-700 text-sm sm:text-base leading-relaxed">
            <div className="flex items-start gap-2">
              <p>{visibleDescription}</p>
            </div>
            {isLongDescription && (
              <button
                className="mt-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                onClick={() => setDescExpanded((v) => !v)}
              >
                {descExpanded ? "Show less" : "Show more"}
              </button>
            )}
          </div>

          {/* CWE Categories with visual grouping */}
          {Object.keys(cweCategories).length > 0 && (
            <div className="mt-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-medium text-gray-600">
                  Vulnerability Types:
                </span>
              </div>
              <div className="flex flex-wrap gap-1">
                {Object.entries(cweCategories).map(([category, cweIds]) => {
                  const cweObj = cweList.find(
                    (item) => item.Name === category || item.ID === category
                  );
                  const description = cweObj
                    ? cweObj.Description
                    : "No description available.";

                  return (
                    <div
                      key={category}
                      className="relative flex items-center gap-1 px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded-md border border-blue-200 cursor-help group/cwe"
                    >
                      <span className="font-medium">{category}</span>
                      <span className="text-blue-500">({cweIds.length})</span>

                      {/* Enhanced Tooltip */}
                      <div className="absolute left-0 bottom-full mb-2 z-20 min-w-[16rem] max-w-xs p-3 bg-gray-800 text-white text-sm rounded shadow-xl opacity-0 group-hover/cwe:opacity-100 pointer-events-none transition-opacity duration-200 transform group-hover/cwe:translate-y-0 translate-y-1 before:absolute before:left-3 before:top-full before:w-0 before:h-0 before:border-l-4 before:border-r-4 before:border-b-0 before:border-t-4 before:border-l-transparent before:border-r-transparent before:border-t-gray-800">
                        <p className="font-semibold mb-1">{category}</p>
                        <p className="text-gray-300">{description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Additional metadata */}
          <div className="mt-3 pt-3 border-t border-gray-100">
            <div
              className="flex items-center justify-between text-xs text-gray-500 cursor-pointer hover:bg-blue-50 rounded px-2 py-1 transition-colors"
              onClick={() => {
                const nistUrl = `https://nvd.nist.gov/vuln/detail/${cve.code || cve.id}`;
                window.open(nistUrl, "_blank", "noopener,noreferrer");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  const nistUrl = `https://nvd.nist.gov/vuln/detail/${cve.code || cve.id}`;
                  window.open(nistUrl, "_blank", "noopener,noreferrer");
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={`View ${cve.code || cve.id} details on NIST NVD`}
            >
              <span>View on NIST NVD</span>
              <div className="flex items-center gap-1 text-blue-500 group-hover:text-blue-700 transition-colors">
                <FontAwesomeIcon icon={faExternalLinkAlt} className="h-3 w-3" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

CVECard.propTypes = {
  cve: PropTypes.shape({
    id: PropTypes.string.isRequired,
    code: PropTypes.string, // CVE code like CVE-2025-12345
    published_at: PropTypes.string,
    updated_at: PropTypes.string,
    description: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.node]),
    exploit_active: PropTypes.bool,
    // Raw API CVSS fields (optional, used for fallback)
    cvss2: PropTypes.string,
    cvss2_score: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    cvss2_severity: PropTypes.string,
    cvss30: PropTypes.string,
    cvss30_score: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    cvss30_severity: PropTypes.string,
    cvss31: PropTypes.string,
    cvss31_score: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    cvss31_severity: PropTypes.string,
    cvss40: PropTypes.string,
    cvss40_score: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    cvss40_severity: PropTypes.string,
    cvss: PropTypes.shape({
      version: PropTypes.string,
      score: PropTypes.number,
      severity: PropTypes.string,
      vector: PropTypes.string,
    }),
    CveCweIds: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        cve_id: PropTypes.string,
        cwe_id: PropTypes.string,
      })
    ),
    eps: PropTypes.shape({
      CVEID: PropTypes.string,
      Score: PropTypes.number,
    }),
    highlightedId: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),  // New: Allow string or array (React elements)
  }).isRequired,
};

export default CVECard;
